/*
 * Copyright (C) 2021 DV Bern AG, Switzerland
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */
import {HttpClient} from '@angular/common/http';
import {Injectable, inject} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {Observable, ReplaySubject} from 'rxjs';
import {map, tap} from 'rxjs/operators';
import {TSSprache} from '../../../../models/enums/TSSprache';
import {TSLastenausgleichTagesschuleAngabenGemeindeContainer} from '../../../../models/gemeindeantrag/TSLastenausgleichTagesschuleAngabenGemeindeContainer';
import {TSLastenausgleichTagesschulenStatusHistory} from '../../../../models/gemeindeantrag/TSLastenausgleichTagesschulenStatusHistory';
import {EbeguRestUtil} from '../../../../utils/EbeguRestUtil';
import {CONSTANTS} from '@models/constants';
import {ErrorService} from '../../../core/errors/service/ErrorService';
import {LogFactory} from '@utils/log';

const LOG = LogFactory.createLog('LastenausgleichTSService');

@Injectable({
    providedIn: 'root'
})
export class LastenausgleichTSService {
    private readonly http = inject(HttpClient);
    private readonly errorService = inject(ErrorService);
    private readonly translate = inject(TranslateService);

    private readonly API_BASE_URL = `${CONSTANTS.REST_API}lats/gemeinde`;
    private readonly ebeguRestUtil = new EbeguRestUtil();
    // return last item but don't provide initial value like BehaviourSubject does
    private lATSAngabenGemeindeContainerStore =
        new ReplaySubject<TSLastenausgleichTagesschuleAngabenGemeindeContainer>(
            1
        );

    public updateLATSAngabenGemeindeContainerStore(id: string): void {
        const url = `${this.API_BASE_URL}/find/${encodeURIComponent(id)}`;
        this.http
            .get<TSLastenausgleichTagesschuleAngabenGemeindeContainer>(url)
            .subscribe({
                next: container => {
                    this.next(container);
                },
                error: error => LOG.error(error)
            });
    }

    public getLATSAngabenGemeindeContainer(): Observable<TSLastenausgleichTagesschuleAngabenGemeindeContainer> {
        return this.lATSAngabenGemeindeContainerStore.asObservable();
    }

    public lATSAngabenGemeindeFuerInstitutionenFreigeben(
        container: TSLastenausgleichTagesschuleAngabenGemeindeContainer
    ): void {
        this.http
            .put<TSLastenausgleichTagesschuleAngabenGemeindeContainer>(
                `${this.API_BASE_URL}/freigebenInstitution`,
                this.ebeguRestUtil.lastenausgleichTagesschuleAngabenGemeindeContainerToRestObject(
                    {},
                    container
                )
            )
            .subscribe({
                next: result => {
                    this.next(result);
                },
                error: err => LOG.error(err)
            });
    }

    public saveLATSAngabenGemeindeContainer(
        container: TSLastenausgleichTagesschuleAngabenGemeindeContainer
    ): void {
        this.errorService.clearAll();
        this.http
            .put<TSLastenausgleichTagesschuleAngabenGemeindeContainer>(
                `${this.API_BASE_URL}/save`,
                this.ebeguRestUtil.lastenausgleichTagesschuleAngabenGemeindeContainerToRestObject(
                    {},
                    container
                )
            )
            .subscribe({
                next: result => {
                    this.errorService.addMesageAsInfo(
                        this.translate.instant('SAVED')
                    );
                    this.next(result);
                },
                error: error => {
                    LOG.error(error);
                }
            });
    }

    public saveLATSKommentar(
        containerId: string,
        kommentar: string
    ): Observable<void> {
        return this.http
            .put<void>(
                `${this.API_BASE_URL}/saveKommentar/${encodeURIComponent(containerId)}`,
                kommentar
            )
            .pipe(
                tap(() =>
                    this.updateLATSAngabenGemeindeContainerStore(containerId)
                )
            );
    }

    public saveLATSVerantworlicher(
        containerId: string,
        username: string
    ): Observable<void> {
        return this.http
            .put<void>(
                `${this.API_BASE_URL}/saveLATSVerantworlicher/${encodeURIComponent(containerId)}`,
                username
            )
            .pipe(
                tap(() =>
                    this.updateLATSAngabenGemeindeContainerStore(containerId)
                )
            );
    }

    public emptyStore(): void {
        this.lATSAngabenGemeindeContainerStore =
            new ReplaySubject<TSLastenausgleichTagesschuleAngabenGemeindeContainer>(
                1
            );
    }

    private next(
        result: TSLastenausgleichTagesschuleAngabenGemeindeContainer
    ): void {
        const savedContainer =
            this.ebeguRestUtil.parseLastenausgleichTagesschuleAngabenGemeindeContainer(
                new TSLastenausgleichTagesschuleAngabenGemeindeContainer(),
                result
            );
        this.lATSAngabenGemeindeContainerStore.next(savedContainer);
    }

    public latsGemeindeAntragFreigeben(
        container: TSLastenausgleichTagesschuleAngabenGemeindeContainer
    ): Observable<TSLastenausgleichTagesschuleAngabenGemeindeContainer> {
        return this.http
            .put<TSLastenausgleichTagesschuleAngabenGemeindeContainer>(
                `${this.API_BASE_URL}/einreichen`,
                this.ebeguRestUtil.lastenausgleichTagesschuleAngabenGemeindeContainerToRestObject(
                    {},
                    container
                )
            )
            .pipe(
                tap(
                    result => {
                        this.next(result);
                    },
                    error => LOG.error(error)
                )
            );
    }

    public latsGemeindeAntragGeprueft(
        container: TSLastenausgleichTagesschuleAngabenGemeindeContainer
    ): Observable<TSLastenausgleichTagesschuleAngabenGemeindeContainer> {
        return this.http
            .put<TSLastenausgleichTagesschuleAngabenGemeindeContainer>(
                `${this.API_BASE_URL}/geprueft`,
                this.ebeguRestUtil.lastenausgleichTagesschuleAngabenGemeindeContainerToRestObject(
                    {},
                    container
                )
            )
            .pipe(
                tap(result => {
                    this.next(result);
                }),
                map(result =>
                    this.ebeguRestUtil.parseLastenausgleichTagesschuleAngabenGemeindeContainer(
                        new TSLastenausgleichTagesschuleAngabenGemeindeContainer(),
                        result
                    )
                )
            );
    }

    public latsGemeindeAntragZurZweitpruefung(
        container: TSLastenausgleichTagesschuleAngabenGemeindeContainer
    ): Observable<TSLastenausgleichTagesschuleAngabenGemeindeContainer> {
        return this.http
            .put(
                `${this.API_BASE_URL}/zur-zweitpruefung/${encodeURIComponent(container.id)}`,
                this.ebeguRestUtil.lastenausgleichTagesschuleAngabenGemeindeContainerToRestObject(
                    {},
                    container
                )
            )
            .pipe(
                map(result =>
                    this.ebeguRestUtil.parseLastenausgleichTagesschuleAngabenGemeindeContainer(
                        new TSLastenausgleichTagesschuleAngabenGemeindeContainer(),
                        result
                    )
                )
            );
    }

    public latsAngabenGemeindeFormularAbschliessen(
        container: TSLastenausgleichTagesschuleAngabenGemeindeContainer
    ): Observable<TSLastenausgleichTagesschuleAngabenGemeindeContainer> {
        return this.http
            .put<TSLastenausgleichTagesschuleAngabenGemeindeContainer>(
                `${this.API_BASE_URL}/gemeinde/abschliessen`,
                this.ebeguRestUtil.lastenausgleichTagesschuleAngabenGemeindeContainerToRestObject(
                    {},
                    container
                )
            )
            .pipe(
                tap(result => this.next(result)),
                map(result =>
                    this.ebeguRestUtil.parseLastenausgleichTagesschuleAngabenGemeindeContainer(
                        new TSLastenausgleichTagesschuleAngabenGemeindeContainer(),
                        result
                    )
                )
            );
    }

    public falscheAngaben(
        container: TSLastenausgleichTagesschuleAngabenGemeindeContainer
    ): void {
        this.http
            .put<TSLastenausgleichTagesschuleAngabenGemeindeContainer>(
                `${this.API_BASE_URL}/falsche-angaben`,
                this.ebeguRestUtil.lastenausgleichTagesschuleAngabenGemeindeContainerToRestObject(
                    {},
                    container
                )
            )
            .subscribe({
                next: reopenendContainer => {
                    this.errorService.clearAll();
                    this.next(reopenendContainer);
                },
                error: err => console.error(err)
            });
    }

    public zurueckAnGemeinde(
        container: TSLastenausgleichTagesschuleAngabenGemeindeContainer
    ): Observable<TSLastenausgleichTagesschuleAngabenGemeindeContainer> {
        return this.http
            .put(
                `${this.API_BASE_URL}/zurueck-an-gemeinde`,
                this.ebeguRestUtil.lastenausgleichTagesschuleAngabenGemeindeContainerToRestObject(
                    {},
                    container
                )
            )
            .pipe(
                map(restContainer =>
                    this.ebeguRestUtil.parseLastenausgleichTagesschuleAngabenGemeindeContainer(
                        new TSLastenausgleichTagesschuleAngabenGemeindeContainer(),
                        restContainer
                    )
                ),
                tap(parsedContainer =>
                    this.updateLATSAngabenGemeindeContainerStore(
                        parsedContainer.id
                    )
                )
            );
    }

    public getVerlauf(
        containerId: string
    ): Observable<TSLastenausgleichTagesschulenStatusHistory[]> {
        return this.http
            .get<any[]>(`${this.API_BASE_URL}/verlauf/${containerId}`)
            .pipe(map(data => this.ebeguRestUtil.parseLatsHistoryList(data)));
    }

    public findAntragOfPreviousPeriode(
        antrag: TSLastenausgleichTagesschuleAngabenGemeindeContainer
    ): Observable<TSLastenausgleichTagesschuleAngabenGemeindeContainer> {
        return this.http
            .get<any>(
                `${this.API_BASE_URL}/previous-antrag/${encodeURIComponent(antrag.id)}`
            )
            .pipe(
                map(restContainer =>
                    this.ebeguRestUtil.parseLastenausgleichTagesschuleAngabenGemeindeContainer(
                        new TSLastenausgleichTagesschuleAngabenGemeindeContainer(),
                        restContainer
                    )
                )
            );
    }

    public getErwarteteBetreuungsstunden(
        antrag: TSLastenausgleichTagesschuleAngabenGemeindeContainer
    ): Observable<number> {
        return this.http.get<number>(
            `${this.API_BASE_URL}/erwartete-betreuungsstunden/${encodeURIComponent(antrag.id)}`
        );
    }

    public getErwarteteBetreuungsstundenPrognose(
        antrag: TSLastenausgleichTagesschuleAngabenGemeindeContainer
    ): Observable<number> {
        return this.http.get<number>(
            `${this.API_BASE_URL}/erwartete-betreuungsstunden-prognose/${encodeURIComponent(antrag.id)}`
        );
    }

    public latsDocxErstellen(
        antrag: TSLastenausgleichTagesschuleAngabenGemeindeContainer,
        sprache: TSSprache,
        betreuungsstundenPrognose: number
    ): Observable<any> {
        return this.http.post(
            `${this.API_BASE_URL}/docx-erstellen/${encodeURIComponent(antrag.id)}/${sprache}`,
            {betreuungsstundenPrognose},
            {responseType: 'blob'}
        );
    }

    public zurueckInPruefung(
        container: TSLastenausgleichTagesschuleAngabenGemeindeContainer
    ): Observable<TSLastenausgleichTagesschuleAngabenGemeindeContainer> {
        return this.http
            .put(
                `${this.API_BASE_URL}/zurueck-in-pruefung`,
                this.ebeguRestUtil.lastenausgleichTagesschuleAngabenGemeindeContainerToRestObject(
                    {},
                    container
                )
            )
            .pipe(
                map(restContainer =>
                    this.ebeguRestUtil.parseLastenausgleichTagesschuleAngabenGemeindeContainer(
                        new TSLastenausgleichTagesschuleAngabenGemeindeContainer(),
                        restContainer
                    )
                ),
                tap(parsedContainer =>
                    this.updateLATSAngabenGemeindeContainerStore(
                        parsedContainer.id
                    )
                )
            );
    }

    public saveLATSAngabenGemeindePrognose(
        containerId: string,
        prognose: number,
        bemerkungen: string
    ): Observable<void> {
        return this.http
            .put<void>(
                `${this.API_BASE_URL}/savePrognose/${encodeURIComponent(containerId)}`,
                {prognose, bemerkungen}
            )
            .pipe(
                tap(
                    () =>
                        this.updateLATSAngabenGemeindeContainerStore(
                            containerId
                        ),
                    error => LOG.error(error)
                )
            );
    }

    public latsGemeindeAntragAbschliessen(
        container: TSLastenausgleichTagesschuleAngabenGemeindeContainer
    ): void {
        this.http
            .put<TSLastenausgleichTagesschuleAngabenGemeindeContainer>(
                `${this.API_BASE_URL}/abschliessen`,
                this.ebeguRestUtil.lastenausgleichTagesschuleAngabenGemeindeContainerToRestObject(
                    {},
                    container
                )
            )
            .subscribe({
                next: result => this.next(result),
                error: error => LOG.error(error)
            });
    }

    public createMissingTagesschuleFormulare(
        gemeindeAngabenId: string
    ): Observable<void> {
        return this.http.post<void>(
            `${this.API_BASE_URL}/create-missing-institutions/${gemeindeAngabenId}`,
            {}
        );
    }

    public generateLATSReport(
        container: TSLastenausgleichTagesschuleAngabenGemeindeContainer
    ): Observable<BlobPart> {
        return this.http.get(`${this.API_BASE_URL}/${container.id}/report`, {
            responseType: 'blob'
        });
    }
}
